<!--
 * @Description: 开票备案-工业发票-工业/服务商。工业只有查看，服务商有上传操作
 * @Date: 2025-06-11 16:21:13
-->

<template>
  <XrkTable
    ref="xrkTableRef"
    :getListAjax="getListAjax"
    :searchConfig="searchConfig"
    :tableConfig="tableConfig"
  ></XrkTable>
</template>
<script lang="tsx" setup>
import { ref } from 'vue';
import {
  XrkTable,
  XrkTableProps,
  useTableColumn,
  useDatePickerColumn,
  BaseButton,
  useSelectColumn,
  useCreateModal
} from 'xrk-components';
import { formatter } from '@/utils';
import { OFFLINE_INVOICE_STATUS } from '@V/invoice/src/constants';
import {
  industryListInvoice,
  listSelectASP,
  listSelectEnterprise,
  gyDownloadBatch
} from '@V/invoice/src/data-source/apis';
import {
  invoiceOrderNoCol,
  invoiceTypeCol,
  aspNameCol,
  invoiceIdentifierNoCol,
  industryNameCol,
  taskAtCol,
  itemNameCol,
  openAtCol,
  invoiceAmountCol
} from '@V/invoice/src/fields/columns';
import InvoiceOfflineDialog from './invoice-offline-dialog';
import { useRouteMeta } from '@hooks/common';
import { useBatchDownload } from '@V/invoice/src/hooks';

const routeMeta = useRouteMeta();
const isGy = [1].includes(+routeMeta.functionCode);
const isAsp = [2].includes(+routeMeta.functionCode);

const xrkTableRef = ref();
const { selectionField, download } = useBatchDownload({
  xrkTableRef,
  ajax: gyDownloadBatch,
  options: { passStatus: [4] }
});

const getListAjax = async params => {
  return industryListInvoice(params);
};

const searchConfig = ref<XrkTableProps['searchConfig']>({
  searchColumns: [
    isGy &&
      useSelectColumn({
        prop: 'aspId',
        label: '销售方',
        config: {
          clearable: true,
          filterUseRemote: true,
          remoteMethod: async name => {
            const res: any = await listSelectASP({
              aspName: name
            });
            return res;
          },
          labelKey: 'aspName',
          valueKey: 'aspId',
          maxlength: 50
        }
      }),
    isGy &&
      useSelectColumn({
        prop: 'invoiceStatus',
        label: '开票状态',
        config: {
          placeholder: '请选择',
          options: OFFLINE_INVOICE_STATUS
        }
      }),
    isAsp &&
      useSelectColumn({
        prop: 'industryId',
        label: '企业名称',
        config: {
          clearable: true,
          filterUseRemote: true,
          remoteMethod: async name => {
            const res: any = await listSelectEnterprise({
              industryName: name
            });
            return res;
          },
          labelKey: 'industryName',
          valueKey: 'industryId',
          maxlength: 50
        }
      }),
    isAsp &&
      useDatePickerColumn({
        prop: 'time',
        label: '更新时间',
        config: {
          type: 'daterange',
          startPlaceholder: '时间始',
          endPlaceholder: '时间止',
          clearable: true
        },
        listener: {
          onChange: (value, searchObj) => {
            if (!value) {
              searchObj.openAtStart = '';
              searchObj.openAtEnd = '';
            } else {
              searchObj.openAtStart = value[0];
              searchObj.openAtEnd = value[1];
            }
          }
        }
      })
  ].filter(item => item),
  searchBtn: {
    show: true,
    text: '搜索'
  },
  resetBtn: {
    show: true,
    text: '重置'
  },
  otherComponents: [
    !isGy && {
      render() {
        return (
          <>
            <BaseButton
              type="primary2"
              click={async () => {
                useCreateModal(InvoiceOfflineDialog, {
                  offlineType: 2,
                  title: '上传发票',
                  onSuccess() {
                    xrkTableRef.value.initCurrentList();
                  }
                });
              }}
            >
              上传发票
            </BaseButton>
          </>
        );
      }
    },
    {
      render() {
        return (
          <>
            <BaseButton
              v-resource={[['批量导出']]}
              type="primary2"
              click={async () => {
                await download();
              }}
            >
              批量导出
            </BaseButton>
          </>
        );
      }
    }
  ].filter(item => item)
});

const tableConfig = ref<XrkTableProps['tableConfig']>({
  tableColumns: [
    selectionField(),
    useTableColumn({
      type: 'index',
      label: '序号',
      width: '80px',
      fixed: 'left'
    }),
    isGy && aspNameCol(),
    isGy && invoiceIdentifierNoCol(),
    isAsp && invoiceOrderNoCol(),
    isAsp && industryNameCol(),
    isAsp && taskAtCol(),
    invoiceAmountCol(isGy ? '价格合计(元)' : '票面金额(元)'),
    itemNameCol(),
    invoiceTypeCol(),
    useTableColumn({
      label: '开票状态',
      prop: 'invoiceStatus',
      formatter: ({ cellValue }) =>
        formatter.defaultFormat(
          cellValue,
          () => OFFLINE_INVOICE_STATUS[cellValue].label
        )
    }),
    openAtCol(),
    useTableColumn({
      label: '操作',
      prop: 'handlerCol',
      fixed: 'right',
      render: ({ row }) => {
        return (
          <>
            {isAsp && (
              <BaseButton
                type="text"
                click={async () => {
                  useCreateModal(InvoiceOfflineDialog, {
                    offlineType: 2,
                    id: row.id,
                    title: '发票详情',
                    onSuccess() {
                      xrkTableRef.value.initCurrentList();
                    }
                  });
                }}
              >
                编辑
              </BaseButton>
            )}
            <BaseButton
              type="text"
              click={async () => {
                useCreateModal(InvoiceOfflineDialog, {
                  offlineType: 1,
                  id: row.id,
                  title: '发票详情'
                });
              }}
            >
              详情
            </BaseButton>
          </>
        );
      }
    })
  ].filter(item => item)
});
</script>
